import React, { useMemo, useState } from 'react';
import { FaChartBar, FaSyncAlt, FaExclamationTriangle, FaShoppingCart, FaUsers, FaBoxOpen, FaCrown } from 'react-icons/fa';
import { useAdminData } from '../../context/AdminDataContext';

const BarFlow = ({ title, subtitle, data, valuePrefix = '', valueSuffix = '', barColorClass = 'fill-orange-500' }) => {
  const max = Math.max(1, ...(data || []).map((d) => Number(d.value) || 0));

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {!!subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Peak</p>
          <p className="text-sm font-semibold text-gray-900">
            {valuePrefix}{Math.round(max).toLocaleString()}{valueSuffix}
          </p>
        </div>
      </div>

      <div className="mt-4">
        {(data || []).length === 0 ? (
          <div className="text-sm text-gray-500 py-8">No data for this range.</div>
        ) : (
          <div className="w-full">
            <svg viewBox={`0 0 ${(data.length * 14) + 10} 60`} className="w-full h-20">
              {(data || []).map((d, idx) => {
                const v = Number(d.value) || 0;
                const h = Math.max(1, Math.round((v / max) * 52));
                const x = 6 + idx * 14;
                const y = 56 - h;
                return (
                  <g key={d.key || idx}>
                    <rect x={x} y={y} width="10" height={h} rx="3" className={barColorClass} opacity={0.9} />
                  </g>
                );
              })}
            </svg>

            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>{data[0]?.label || ''}</span>
              <span>{data[data.length - 1]?.label || ''}</span>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {(data || []).slice(-4).map((d, idx) => (
                <div key={`${d.key || idx}-stat`} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-xs text-gray-500">{d.label}</p>
                  <p className="text-sm font-semibold text-gray-900">{valuePrefix}{(Number(d.value) || 0).toLocaleString()}{valueSuffix}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Reports = () => {
  const { items, users, orders, loading, error, loadedAt, refresh } = useAdminData();
  const [range, setRange] = useState('30d');

  const now = Date.now();

  const rangeStart = useMemo(() => {
    if (range === '7d') return now - 7 * 24 * 60 * 60 * 1000;
    if (range === '30d') return now - 30 * 24 * 60 * 60 * 1000;
    if (range === '90d') return now - 90 * 24 * 60 * 60 * 1000;
    return 0;
  }, [range, now]);

  const startForCharts = useMemo(() => {
    // Keep charts readable; cap to 30 days even if user selects "all"
    if (!rangeStart || range === 'all') return now - 30 * 24 * 60 * 60 * 1000;
    return rangeStart;
  }, [rangeStart, range, now]);

  const filteredOrders = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    if (!rangeStart) return list;
    return list.filter((o) => {
      const t = o?.created_at ? new Date(o.created_at).getTime() : 0;
      return t >= rangeStart;
    });
  }, [orders, rangeStart]);

  const keyForDay = (dateInput) => {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const labelForDay = (key) => {
    if (!key) return '';
    const [y, m, d] = String(key).split('-');
    return `${d}/${m}`;
  };

  const buildDayKeys = (startMs, endMs) => {
    const keys = [];
    const start = new Date(startMs);
    const end = new Date(endMs);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    for (let t = start.getTime(); t <= end.getTime(); t += 24 * 60 * 60 * 1000) {
      const k = keyForDay(new Date(t));
      if (k) keys.push(k);
    }
    return keys;
  };

  const toNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const calculateTotal = (order) => {
    if (!order) return 0;
    const direct =
      toNumber(order.total_price) ||
      toNumber(order.total_amount) ||
      toNumber(order.total) ||
      toNumber(order.amount);
    if (direct > 0) return direct;

    const its = Array.isArray(order.items) ? order.items : [];
    const itemsTotal = its.reduce((sum, it) => {
      const price = toNumber(it?.item?.price ?? it?.price ?? it?.unit_price ?? it?.amount);
      const qty = toNumber(it?.quantity) || 1;
      return sum + price * qty;
    }, 0);

    const shipping = toNumber(order.shipping_cost || order.shipping);
    const tax = toNumber(order.tax_amount || order.tax);
    const total = itemsTotal + shipping + tax;
    return total > 0 ? total : 0;
  };

  const itemNameById = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(items) ? items : [];
    for (const it of list) {
      const id = it?.id;
      const name = it?.name || it?.title || it?.product_name;
      if (id != null && name) map.set(String(id), name);
    }
    return map;
  }, [items]);

  const getOrderPrimaryItemLabel = (order) => {
    const its = Array.isArray(order?.items) ? order.items : [];
    if (its.length === 0) return 'No items';
    const first = its[0];
    const id = first?.item?.id || first?.item;
    const rawName = first?.item?.name || first?.name || first?.item?.title || first?.item?.product_name;
    const fallbackName = (id != null && itemNameById.get(String(id))) ? itemNameById.get(String(id)) : null;
    const name = rawName || fallbackName || (id != null ? `Item ${id}` : 'Item');
    const qty = Number(first?.quantity) || 1;
    const more = its.length > 1 ? ` +${its.length - 1} more` : '';
    return `${name} (x${qty})${more}`;
  };

  const metrics = useMemo(() => {
    const o = filteredOrders;

    const revenue = o.reduce((sum, ord) => sum + calculateTotal(ord), 0);
    const totalOrders = o.length;
    const avgOrderValue = totalOrders > 0 ? revenue / totalOrders : 0;

    const byStatus = o.reduce((acc, ord) => {
      const s = String(ord?.status || 'unknown').toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});

    const productAgg = o.reduce((acc, ord) => {
      const its = Array.isArray(ord?.items) ? ord.items : [];
      for (const it of its) {
        const id = it?.item?.id || it?.item;
        const rawName = it?.item?.name || it?.name || it?.item?.title || it?.item?.product_name;
        const mappedName = (id != null && itemNameById.get(String(id))) ? itemNameById.get(String(id)) : null;
        const name = rawName || mappedName || `Item ${id || ''}`.trim();
        const key = String(id || name);
        const qty = toNumber(it?.quantity) || 1;
        acc[key] = acc[key] || { id, name, qty: 0 };
        acc[key].qty += qty;
      }
      return acc;
    }, {});

    const topProducts = Object.values(productAgg)
      .sort((a, b) => (b.qty || 0) - (a.qty || 0))
      .slice(0, 5);

    const recentOrders = [...o]
      .sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 8);

    const recentSales = [...o]
      .sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 10);

    const chartDayKeys = buildDayKeys(startForCharts, now);

    const salesByDayMap = chartDayKeys.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});
    for (const ord of (Array.isArray(orders) ? orders : [])) {
      const t = ord?.created_at ? new Date(ord.created_at).getTime() : 0;
      if (!t || t < startForCharts) continue;
      const k = keyForDay(new Date(t));
      if (!k || salesByDayMap[k] == null) continue;
      salesByDayMap[k] += calculateTotal(ord);
    }
    const salesByDay = chartDayKeys.map((k) => ({ key: k, label: labelForDay(k), value: Math.round(salesByDayMap[k] || 0) }));

    const usersList = Array.isArray(users) ? users : [];
    const registrationsByDayMap = chartDayKeys.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});
    for (const u of usersList) {
      const ts = u?.created_at || u?.date_joined || u?.joined_at || u?.created || u?.timestamp;
      if (!ts) continue;
      const t = new Date(ts).getTime();
      if (!t || t < startForCharts) continue;
      const k = keyForDay(new Date(t));
      if (!k || registrationsByDayMap[k] == null) continue;
      registrationsByDayMap[k] += 1;
    }
    const registrationsByDay = chartDayKeys.map((k) => ({ key: k, label: labelForDay(k), value: registrationsByDayMap[k] || 0 }));

    const itemsList = Array.isArray(items) ? items : [];
    const recentItems = [...itemsList]
      .sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : (Number(a?.id) || 0);
        const tb = b?.created_at ? new Date(b.created_at).getTime() : (Number(b?.id) || 0);
        return tb - ta;
      })
      .slice(0, 8);

    const customerAgg = (Array.isArray(orders) ? orders : []).reduce((acc, ord) => {
      const userId = ord?.user?.id || ord?.buyer || ord?.user;
      const email = ord?.user?.email || ord?.shipping?.email || '';
      const key = String(userId || email || `order-${ord?.id}`);
      const name = (ord?.user?.first_name || ord?.user?.last_name)
        ? `${ord.user?.first_name || ''} ${ord.user?.last_name || ''}`.trim()
        : ((ord?.shipping?.first_name || ord?.shipping?.last_name)
          ? `${ord.shipping?.first_name || ''} ${ord.shipping?.last_name || ''}`.trim()
          : (email || 'Customer'));
      acc[key] = acc[key] || { key, name, email, orders: 0, spent: 0 };
      acc[key].orders += 1;
      acc[key].spent += calculateTotal(ord);
      return acc;
    }, {});

    const frequentCustomers = Object.values(customerAgg)
      .sort((a, b) => (b.orders - a.orders) || (b.spent - a.spent))
      .slice(0, 6);

    const totalProducts = Array.isArray(items) ? items.length : 0;
    const totalUsers = Array.isArray(users) ? users.length : 0;

    return {
      revenue,
      totalOrders,
      avgOrderValue,
      byStatus,
      topProducts,
      recentOrders,
      recentSales,
      salesByDay,
      registrationsByDay,
      recentItems,
      frequentCustomers,
      totalProducts,
      totalUsers,
    };
  }, [filteredOrders, items, users, orders, startForCharts, now, itemNameById]);

  const formatDateTime = (iso) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString('en-KE', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const formatLoadedAt = () => {
    if (!loadedAt) return 'N/A';
    const d = new Date(loadedAt);
    return d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <FaChartBar className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
              <p className="text-sm text-gray-600">Sales, orders, and product performance summary</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Range</span>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          <button
            onClick={refresh}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
          >
            <FaSyncAlt className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-gray-500">Last updated: {formatLoadedAt()}</p>
      </div>

      {error && (
        <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 flex items-start gap-3">
          <FaExclamationTriangle className="mt-1" />
          <div>
            <p className="font-semibold">Failed to load reports</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <p className="text-sm text-gray-600">Revenue</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">KES {metrics.revenue.toLocaleString()}</p>
          <p className="mt-1 text-xs text-gray-500">Based on order totals</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <p className="text-sm text-gray-600">Orders</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{metrics.totalOrders.toLocaleString()}</p>
          <p className="mt-1 text-xs text-gray-500">In selected range</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <p className="text-sm text-gray-600">Avg. Order Value</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">KES {Math.round(metrics.avgOrderValue).toLocaleString()}</p>
          <p className="mt-1 text-xs text-gray-500">Revenue / Orders</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <p className="text-sm text-gray-600">Catalog / Users</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {metrics.totalProducts.toLocaleString()} / {metrics.totalUsers.toLocaleString()}
          </p>
          <p className="mt-1 text-xs text-gray-500">Products / Users</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <BarFlow
          title="Sales per Day"
          subtitle="Daily revenue flow (last 30 days)"
          data={metrics.salesByDay}
          valuePrefix="KES "
          barColorClass="fill-orange-500"
        />
        <BarFlow
          title="People Registered"
          subtitle="New user registrations per day (last 30 days)"
          data={metrics.registrationsByDay}
          valueSuffix=""
          barColorClass="fill-emerald-500"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Most Recent Sales</h3>
            <FaShoppingCart className="text-gray-400" />
          </div>
          <div className="mt-4 space-y-3">
            {metrics.recentSales.length === 0 ? (
              <p className="text-sm text-gray-500">No sales found.</p>
            ) : (
              metrics.recentSales.slice(0, 8).map((o) => (
                <div key={`sale-${o.id}`} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{getOrderPrimaryItemLabel(o)}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(o.created_at)} • Order #{o.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-orange-600">KES {calculateTotal(o).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 capitalize">{String(o.status || 'unknown').replace('_', ' ')}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recently Added Items</h3>
            <FaBoxOpen className="text-gray-400" />
          </div>
          <div className="mt-4 space-y-3">
            {(metrics.recentItems || []).length === 0 ? (
              <p className="text-sm text-gray-500">No items found.</p>
            ) : (
              metrics.recentItems.map((it, idx) => (
                <div key={`item-${it?.id || idx}`} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{it?.name || it?.title || 'Item'}</p>
                    <p className="text-xs text-gray-500">{it?.category || 'Uncategorized'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">KES {toNumber(it?.price || it?.regular_price || it?.amount).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{it?.created_at ? formatDateTime(it.created_at) : `ID ${it?.id ?? ''}`}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Frequent Customers</h3>
            <FaCrown className="text-gray-400" />
          </div>
          <div className="mt-4 space-y-3">
            {(metrics.frequentCustomers || []).length === 0 ? (
              <p className="text-sm text-gray-500">No customer stats yet.</p>
            ) : (
              metrics.frequentCustomers.map((c) => (
                <div key={c.key} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-500 truncate">{c.email || '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{c.orders} orders</p>
                    <p className="text-xs text-gray-500">KES {Math.round(c.spent).toLocaleString()}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
            <FaUsers className="text-gray-400" />
            Computed from orders history
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Orders by Status</h3>
          <div className="mt-4 space-y-2">
            {Object.keys(metrics.byStatus).length === 0 ? (
              <p className="text-sm text-gray-500">No orders in this range.</p>
            ) : (
              Object.entries(metrics.byStatus)
                .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                .map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">{status.replace('_', ' ')}</span>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Top Products</h3>
          <div className="mt-4 space-y-3">
            {metrics.topProducts.length === 0 ? (
              <p className="text-sm text-gray-500">No product data available.</p>
            ) : (
              metrics.topProducts.map((p, idx) => (
                <div key={`${p.id || p.name}-${idx}`} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 line-clamp-1">{p.name || 'Product'}</p>
                    <p className="text-xs text-gray-500">Sold Qty</p>
                  </div>
                  <span className="text-sm font-semibold text-orange-600">{p.qty}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-2 pr-4">Order</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Total</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recentOrders.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-4 text-sm text-gray-500">No recent orders.</td>
                  </tr>
                ) : (
                  metrics.recentOrders.map((o) => (
                    <tr key={o.id} className="border-t">
                      <td className="py-2 pr-4 text-sm text-gray-900">#{o.id}</td>
                      <td className="py-2 pr-4 text-sm text-gray-700 capitalize">{String(o.status || 'unknown').replace('_', ' ')}</td>
                      <td className="py-2 pr-4 text-sm font-semibold text-gray-900">KES {calculateTotal(o).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-500">Updated: {formatDateTime(new Date().toISOString())}</p>
        </div>
      </div>

      {loading && (
        <div className="mt-6 text-sm text-gray-500">Loading reports...</div>
      )}
    </div>
  );
};

export default Reports;
